import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { roleLabel } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { useSettings } from '../settings/SettingsContext.js';
import { ADMIN_PERMS, navLeaves, type NavItem } from '../app/nav.js';
import { useNavModel } from '../app/useNavModel.js';
import { LoadingState } from './ui.js';
import { Login } from '../pages/Login.js';
import { LogoMark } from './Logo.js';
import { BootAnimation } from './BootAnimation.js';
import { Avatar } from './Avatar.js';
import { FirstRunThemeModal } from './FirstRunThemeModal.js';
import { FirstRunPaletteModal } from './FirstRunPaletteModal.js';
import { AnnouncementsModal } from './AnnouncementsModal.js';
import { CursorHalo } from './CursorHalo.js';
import { NotificationsBell } from './NotificationsBell.js';
import { FetchingLogo } from './FetchingLogo.js';
import { PendingApproval } from '../pages/PendingApproval.js';
import { CollabProvider } from '../collab/CollabContext.js';
import { CollabDock } from '../collab/CollabDock.js';
import { ChatPanel } from '../collab/ChatPanel.js';
import { NudgeToasts } from '../collab/NudgeToasts.js';
import { CoopCursors } from '../collab/CoopCursors.js';
import { AccountSheet } from './AccountSheet.js';
import { UserMenu } from './UserMenu.js';
import { NavCount, NavRail, sumBadges, useNavCounts, type NavBadge } from './NavRail.js';
import { TopSearch } from './TopSearch.js';
import { IconChevronRight, IconMenu } from './icons.js';
import { useBackToClose, useIsCoarse, useIsCompact, useKeyboardInset } from '../lib/media.js';

// the PC rail's collapsed state, remembered per device. Storage can be blocked (private mode, a locked-down
// browser): then the rail simply starts open.
const RAIL_KEY = 'rolodex.railCollapsed';
function readRailCollapsed(): boolean {
  try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
}
function writeRailCollapsed(v: boolean) {
  try { localStorage.setItem(RAIL_KEY, v ? '1' : '0'); } catch { /* ignore quota / privacy mode */ }
}

/** navigate out of an open overlay (nav menu, bell, account sheet). On touch, useBackToClose gave the
 *  overlay its own history entry, so the destination REPLACES that entry and the overlay closes once the
 *  router has moved: Back from the new page then lands on the page underneath, with no stray duplicate.
 *  (Closing first would race the overlay's own history.back() against the router's push.) Without that
 *  entry, i.e. on desktop, it is the plain close-then-navigate. */
function useLeaveOverlay() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  return (path: string, close: () => void) => {
    const owned = (window.history.state as { __overlay?: number } | null)?.__overlay != null;
    if (!owned) { close(); if (path) navigate(path); return; }
    if (!path || path === pathname + search) { close(); return; } // staying put: closing pops our entry
    void Promise.resolve(navigate(path, { replace: true })).finally(close);
  };
}

export function AppShell() {
  const { me, loading, can, logout } = useAuth();
  const { leafVisible, groups, findPages } = useNavModel();
  const { activeSeason, currentSeason, seasons, setActiveSeason, isCurrent } = useSeason();
  const { prefs, uiTheme } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const [boot, setBoot] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const leaveOverlay = useLeaveOverlay();
  // the rail's row counts (cache only), repeated in the nav menu
  const counts = useNavCounts();
  // compact (< 1024px): the bar condenses and the avatar opens the Account sheet. coarse: touch, where
  // the menu search must not raise the keyboard on open.
  const compact = useIsCompact();
  const coarse = useIsCoarse();
  // PC (≥1024px) gets the L-shell: the rail holds the navigation unless collapsed, and the nav menu
  // trigger returns only when it is. Below that the shipped condensed bar is untouched.
  const showRail = !compact && !railCollapsed;
  const toggleRail = () => setRailCollapsed((c) => { writeRailCollapsed(!c); return !c; });
  useKeyboardInset(); // --kb-inset for bottom sheets riding above the iOS keyboard (touch only)
  const closeMenu = () => { setMenuOpen(false); setNavQuery(''); };
  useBackToClose(menuOpen, closeMenu);
  // with a mouse the search field takes focus (autoFocus); on touch the menu itself does, so Escape and
  // Tab stay inside it without raising the on-screen keyboard
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (menuOpen && coarse) navRef.current?.focus({ preventScroll: true });
  }, [menuOpen, coarse]);
  // the rail coming back (expanded, or the window widened) takes over from an open nav menu
  useEffect(() => {
    if (showRail && menuOpen) { setMenuOpen(false); setNavQuery(''); }
  }, [showRail]); // eslint-disable-line react-hooks/exhaustive-deps

  // play the animus boot sequence once per browser session, right after sign-in.
  useEffect(() => {
    if (me && !sessionStorage.getItem('rolodex_boot_seen')) {
      sessionStorage.setItem('rolodex_boot_seen', '1');
      setBoot(true);
    }
  }, [me]);

  if (loading) return <LoadingState label="Signing in…" />;

  if (!me) return <Login />;

  if (me.user.pending) return <PendingApproval />;

  const go = (path: string) => leaveOverlay(path, closeMenu);

  const currentLabel = navLeaves().find((i) => i.path === location.pathname)?.label ?? 'Menu';
  // Administration is reachable only by roles that hold an admin permission — administrators and,
  // via admin:config, moderators. On PC it sits in the user menu; the compact bar keeps its shield (tablets)
  // and the Account sheet; hidden for everyone else.
  const isAdmin = ADMIN_PERMS.some((p) => can(p));

  // visibility + type-to-find come from the shared nav model (the rail and the top-bar search use it too)
  const navQ = navQuery.trim().toLowerCase();
  const navResults = navQ ? findPages(navQuery) : [];
  const openLeaf = (leaf: NavItem) => {
    if (leaf.external) { window.open(leaf.external, '_blank', 'noopener,noreferrer'); closeMenu(); }
    else if (leaf.path) go(leaf.path);
  };
  // below the desktop floor the avatar opens the Account sheet (season, Settings, Admin, co-op, Log out);
  // at desktop widths it opens the user menu (Settings, Administration, colour, Log out).
  const openAccount = () => setAccountOpen(true);

  return (
    <CollabProvider>
    <div className={`app${compact ? '' : ' is-pc'}${showRail ? ' has-rail' : ''}`}>
      {boot && <BootAnimation onDone={() => setBoot(false)} />}
      {/* a theme can keep the halo off (Stegi, High contrast) without touching the user's own choice */}
      {prefs.cursorHalo && uiTheme.cursorHalo && <CursorHalo />}
      <NudgeToasts />
      <CoopCursors />
      <ChatPanel />
      {/* held until the boot finishes, so its click-to-skip doesn't land on a popup's scrim */}
      {!boot && (
        <>
          <FirstRunThemeModal />
          <FirstRunPaletteModal />
          <AnnouncementsModal />
        </>
      )}
      <header className="topbar">
        {/* PC: show / hide the navigation rail (remembered on this device) */}
        {!compact && (
          <button
            type="button"
            className="rail-toggle"
            onClick={toggleRail}
            aria-expanded={showRail}
            aria-controls="nav-rail"
            aria-label={showRail ? 'Hide navigation rail' : 'Show navigation rail'}
            title={showRail ? 'Hide navigation rail' : 'Show navigation rail'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3.5 4.5h17v15h-17zM9.5 4.5v15" />
              {showRail && <path d="M6 8.5h1M6 11.5h1M6 14.5h1" />}
            </svg>
          </button>
        )}
        <div className="topbar-brand">
          <div className="mark">
            <LogoMark size={38} themed />
          </div>
          <div>
            <div className="name">ROLODEX</div>
            <div className="sub">Technohull · Assembly</div>
          </div>
        </div>

        {(compact || railCollapsed) && (
          <button
            ref={triggerRef}
            className="nav-trigger"
            onClick={() => { setMenuOpen((o) => !o); setNavQuery(''); }}
            aria-expanded={menuOpen}
            aria-label={currentLabel === 'Menu' ? 'Menu' : `Menu: ${currentLabel}`}
          >
            <IconMenu size={18} /> <span className="nav-trigger-label">{currentLabel}</span>
          </button>
        )}

        {!compact && <TopSearch />}

        <div className="topbar-spacer" />

        <div className="topbar-actions">
          <FetchingLogo />
          <CollabDock />
          {seasons.length > 1 ? (
            <select
              className={`topbar-season${isCurrent ? '' : ' past'}`}
              value={activeSeason}
              onChange={(e) => setActiveSeason(e.target.value)}
              aria-label="Season"
              title={isCurrent ? 'Active season' : `Viewing ${activeSeason} - current season is ${currentSeason}`}
            >
              {/* compact bar: the short label keeps the closed select ~90px */}
              {seasons.map((id) => (
                <option key={id} value={id}>
                  {compact
                    ? `${id}${id === currentSeason ? ' · current' : ''}`
                    : `Season ${id}${id === currentSeason ? ' (current)' : ''}`}
                </option>
              ))}
            </select>
          ) : (
            <span className="muted" style={{ color: 'rgba(255,255,255,.55)' }}>Season {activeSeason}</span>
          )}
          {/* phones drop the select; viewing another season still shows, as an amber chip that opens the sheet */}
          {!isCurrent && (
            <button
              className="topbar-season-chip"
              onClick={openAccount}
              aria-label={`Viewing season ${activeSeason}. Change season`}
            >
              {activeSeason}
            </button>
          )}
          {/* PC: Administration lives in the user menu */}
          {isAdmin && compact && (
            <button
              className={`btn btn-sm btn-ghost topbar-admin ${location.pathname.startsWith('/admin') ? 'on' : ''}`}
              onClick={() => navigate('/admin')}
              title="Administration"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', flexShrink: 0 }} aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span className="topbar-admin-label">Admin</span>
            </button>
          )}
          <NotificationsBell onNavigate={leaveOverlay} />
          {/* compact: the avatar opens the Account sheet. PC: the user menu (Log out moved into it) */}
          {compact ? (
            <button
              className="user user-btn"
              onClick={openAccount}
              title="Profile and settings"
              aria-label={`Account: ${me.user.displayName}`}
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
            >
              <Avatar name={me.user.displayName} avatar={me.user.avatar} size={30} />
              <div style={{ textAlign: 'left' }}>
                <div>{me.user.displayName}</div>
                <div className="sub">{roleLabel(me.user.role)}</div>
              </div>
            </button>
          ) : (
            <UserMenu onNavigate={leaveOverlay} />
          )}
        </div>
      </header>

      {menuOpen && (
        <>
          {/* click-catcher: invisible on desktop, a dim scrim behind the phone sheet */}
          <div className="nav-scrim" onClick={() => setMenuOpen(false)} />
          {/* Escape anywhere in the menu closes it and hands focus back to the trigger */}
          <nav
            ref={navRef}
            tabIndex={-1}
            className="nav-menu"
            aria-label="Pages"
            onKeyDown={(e) => {
              if (e.key === 'Escape') { closeMenu(); triggerRef.current?.focus(); }
            }}
          >
            {/* the bar pins the search on the phone sheet while the list scrolls under it */}
            <div className="nav-search-bar">
              <input
                className="nav-search"
                type="search"
                // type-to-find on open with a mouse; on touch it would raise the keyboard over the list
                autoFocus={!coarse}
                value={navQuery}
                placeholder="Search pages…"
                aria-label="Search pages"
                enterKeyHint="go"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setNavQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && navResults[0]) openLeaf(navResults[0].leaf);
                }}
              />
            </div>
            {navQ ? (
              <div className="nav-results">
                {navResults.length === 0 ? (
                  <div className="nav-empty">No pages match “{navQuery}”.</div>
                ) : (
                  navResults.map(({ leaf, group }) => (
                    <button
                      key={leaf.path ?? leaf.external ?? leaf.label}
                      className={`nav-item ${leaf.path && location.pathname === leaf.path ? 'active' : ''}`}
                      aria-current={leaf.path && location.pathname === leaf.path ? 'page' : undefined}
                      onClick={() => openLeaf(leaf)}
                    >
                      {leaf.label}{leaf.external ? ' ↗' : ''}
                      <NavCount badge={leaf.path ? counts[leaf.path] : undefined} />
                      <span className="nav-item-grp">{group}</span>
                    </button>
                  ))
                )}
              </div>
            ) : (
            <div className="nav-cols">
            {groups.map(({ group, items }) => {
              return (
                <div key={group.title} className="nav-group">
                  <div className="nav-group-hd">{group.title}</div>
                  {items.map((i) =>
                    i.children ? (
                      <SubMenu
                        key={i.label}
                        item={i}
                        pathname={location.pathname}
                        leafVisible={leafVisible}
                        onGo={go}
                        counts={counts}
                      />
                    ) : i.external ? (
                      <a
                        key={i.label}
                        className="nav-item"
                        href={i.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMenuOpen(false)}
                      >
                        {i.label} ↗
                      </a>
                    ) : (
                      <button
                        key={i.path}
                        className={`nav-item ${location.pathname === i.path ? 'active' : ''}`}
                        aria-current={location.pathname === i.path ? 'page' : undefined}
                        onClick={() => go(i.path!)}
                      >
                        {i.label}
                        {i.soon && <span className="muted" style={{ marginLeft: 6 }}>· soon</span>}
                        <NavCount badge={i.path ? counts[i.path] : undefined} />
                      </button>
                    ),
                  )}
                </div>
              );
            })}
            {/* the compact bar hides Log out (and Admin on phones), so the menu repeats them at its foot */}
            {compact && (
              <div className="nav-group nav-group-you">
                <div className="nav-group-hd">You</div>
                <button
                  className={`nav-item ${location.pathname === '/settings' ? 'active' : ''}`}
                  aria-current={location.pathname === '/settings' ? 'page' : undefined}
                  onClick={() => go('/settings')}
                >
                  Settings
                </button>
                {isAdmin && (
                  <button
                    className={`nav-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                    aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                    onClick={() => go('/admin')}
                  >
                    Administration
                  </button>
                )}
                <button className="nav-item" onClick={() => { closeMenu(); void logout(); }}>
                  Log out
                </button>
              </div>
            )}
            </div>
            )}
          </nav>
        </>
      )}

      {accountOpen && <AccountSheet onClose={() => setAccountOpen(false)} onNavigate={leaveOverlay} />}

      <div className="app-body">
        {showRail && <NavRail id="nav-rail" />}
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
    </CollabProvider>
  );
}

function SubMenu({
  item,
  pathname,
  leafVisible,
  onGo,
  counts,
}: {
  item: NavItem;
  pathname: string;
  leafVisible: (i: NavItem) => boolean;
  onGo: (path: string) => void;
  counts: Record<string, NavBadge>;
}) {
  const children = (item.children ?? []).filter(leafVisible);
  const hasActive = children.some((c) => c.path === pathname);
  const [open, setOpen] = useState(hasActive);
  const coarse = useIsCoarse();

  return (
    <div className="nav-sub">
      <button className={`nav-item nav-sub-hd ${open ? 'open' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {/* one chevron, turned 90° when open (CSS); bigger for a finger */}
        <span className="nav-sub-caret" aria-hidden="true"><IconChevronRight size={coarse ? 16 : 10} strokeWidth={2.4} /></span>
        {item.label}
        {!open && <NavCount badge={sumBadges(children.map((c) => (c.path ? counts[c.path] : undefined)))} />}
      </button>
      {open &&
        children.map((c) =>
          c.external ? (
            <a
              key={c.label}
              className="nav-item nav-sub-item"
              href={c.external}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onGo('')}
            >
              {c.label} ↗
            </a>
          ) : (
            <button
              key={c.path}
              className={`nav-item nav-sub-item ${pathname === c.path ? 'active' : ''}`}
              aria-current={pathname === c.path ? 'page' : undefined}
              onClick={() => onGo(c.path!)}
            >
              {c.label}
              {c.soon && <span className="muted" style={{ marginLeft: 6 }}>· soon</span>}
              <NavCount badge={c.path ? counts[c.path] : undefined} />
            </button>
          ),
        )}
    </div>
  );
}
